import {
  areaById, areasOf, artRectIn, clamp, parseColor, printClipped, titleCaseId, toHex,
  type Backdrop, type InkLook, type MockupJob, type MockupPrint, type MockupTemplate, type MockupView, type Placement, type PrintArea, type Rgb,
} from '@eip/engine';
import type { RunResult } from '../worker/protocol';
import { OFFSET_IN_MAX, OFFSET_IN_MIN, sanitizeJob, WIDTH_IN_MAX, WIDTH_IN_MIN } from '../state/mockup';
import type { DocEntry } from '../state/store';
import { candidatesFor, snapGarment, type TemplateSource } from './templates';

/** The panel caps a document at this many colourways — the ninth toggle is a
 *  no-op, and the panel shows a small "Up to 8 colours" hint once it is hit. */
export const MAX_MOCKUP_COLORS = 8;

/**
 * The template `<select>`'s grouped options: one `<optgroup>` per built-in
 * style (in `groups`' own order — built-in-first, as the registry merges
 * them), holding only that style's BUILT-IN blanks, then a single trailing
 * "My templates" group collecting every user template regardless of which
 * style it belongs to (a duplicated blank moves here — see Task 5's
 * "Duplicate to My templates" — so it has to read as one flat list, not
 * scattered back into the style groups it came from).
 *
 * FRONT-view templates only: `job.templateId` is always the front blank the
 * print goes onto — a style's back blank is chosen automatically (by style,
 * from `job.templateId`'s own style) for the "Back design" select instead of
 * appearing here as a separate, pickable front template.
 */
export function templateOptions(
  groups: Array<{ style: string; styleName: string; templates: TemplateSource[] }>,
): Array<{ label: string; options: Array<{ value: string; label: string }> }> {
  const out: Array<{ label: string; options: Array<{ value: string; label: string }> }> = [];
  const mine: Array<{ value: string; label: string }> = [];
  for (const g of groups) {
    const front = g.templates.filter((t) => t.template.view === 'front');
    const builtins = front.filter((t) => t.kind === 'builtin');
    if (builtins.length) out.push({ label: g.styleName, options: builtins.map((t) => ({ value: t.template.id, label: t.template.name })) });
    for (const t of front) if (t.kind === 'user') mine.push({ value: t.template.id, label: t.template.name });
  }
  if (mine.length) out.push({ label: 'My templates', options: mine });
  return out;
}

/**
 * The garment-colour swatches for one style and view: one per blank the style
 * has of that view, in registry order — recolourable or not, since even a
 * non-recolourable blank is still a perfectly good colour to select (its OWN
 * photographed colour, `snapGarment`'s job to recognise on the way back in).
 */
export function styleColorOptions(sources: TemplateSource[], style: string, view: MockupView): Array<{ hex: string; name: string; templateId: string }> {
  return candidatesFor(sources, style, view).map((t) => ({ hex: toHex(t.garmentColor), name: t.colorName, templateId: t.id }));
}

/**
 * The colourway list after a swatch click: `hex` goes ON SCREEN. `colors[0]` is
 * the colour on screen (`garmentForColors` reads it), the rest are selected for
 * the sheet. An unselected colour is added at the front (up to
 * `MAX_MOCKUP_COLORS`, past which this is a no-op — the panel explains why); a
 * selected one moves to the front, still selected; the one already on screen
 * is left alone. A click never removes a colour — that is `removeColor`, behind
 * each selected swatch's own × — because a click that sometimes previewed and
 * sometimes deleted was the least intuitive thing in the panel (hands-on,
 * 2026-09-26).
 */
export function showColor(colors: string[], hex: string): string[] {
  const i = colors.indexOf(hex);
  if (i === 0) return colors;
  if (i > 0) return [hex, ...colors.slice(0, i), ...colors.slice(i + 1)];
  if (colors.length >= MAX_MOCKUP_COLORS) return colors;
  return [hex, ...colors];
}

/** Takes `hex` out of the colourways; when it was the colour on screen, the next
 *  in line takes the screen. A colour that is not selected is left alone. */
export function removeColor(colors: string[], hex: string): string[] {
  const i = colors.indexOf(hex);
  return i < 0 ? colors : [...colors.slice(0, i), ...colors.slice(i + 1)];
}

/**
 * The full swatch list `ColorSwatches` renders: every style blank from
 * `options` (whether or not it is currently selected — these are always
 * offered), `custom: false`, in `options`' own order; then a `custom: true`
 * chip (no `templateId` — nothing in the registry to point at) for every
 * selected colour `options` doesn't cover, so a hand-typed or once-snapped-
 * elsewhere colour stays visible (and, since it is by construction already
 * in `colors`, removable the same way any other swatch is — a click toggles
 * it back out).
 */
export function swatchList(
  colors: string[],
  options: Array<{ hex: string; name: string; templateId: string }>,
): Array<{ hex: string; name: string; templateId?: string; custom: boolean }> {
  const known = new Set(options.map((o) => o.hex));
  const extra = colors.filter((c) => !known.has(c)).map((hex) => ({ hex, name: hex, custom: true as const }));
  return [...options.map((o) => ({ hex: o.hex, name: o.name, templateId: o.templateId, custom: false as const })), ...extra];
}

/**
 * The chosen colourways as chips, in the order the sheet prints them: the hex
 * and the name to show beside it — the blank's own colour name where one of the
 * style's blanks wears it, and the hex itself for a colour typed by hand (which
 * is the only name it has).
 *
 * `colors[0]` is the one on screen; the caller marks it. The order is the
 * colours' own, never the swatch row's — the chips are what the shop reads back
 * as "these are the colourways I am ordering".
 */
export function colorChips(
  colors: string[],
  options: Array<{ hex: string; name: string; templateId: string }>,
): Array<{ hex: string; name: string }> {
  return colors.map((hex) => ({ hex, name: options.find((o) => o.hex === hex)?.name ?? hex }));
}

/** Accepts `#rgb`/`rgb` or `#rrggbb`/`rrggbb` (either with or without the
 *  leading `#`), case-insensitively, and normalizes to a lower-case
 *  `#rrggbb`; anything else is `null`. A superset of `SettingsBar`'s
 *  `normalizeHex` — this one also expands the 3-digit shorthand a swatch
 *  card's colour is often quoted in. */
export function normalizeHex(input: string): string | null {
  const t = input.trim().replace(/^#/, '').toLowerCase();
  if (/^[0-9a-f]{3}$/.test(t)) return `#${t[0]}${t[0]}${t[1]}${t[1]}${t[2]}${t[2]}`;
  if (/^[0-9a-f]{6}$/.test(t)) return `#${t}`;
  return null;
}

/** Applies a placement edit, clamping whichever fields the patch touches to
 *  the same ranges the persisted defaults are held to (`WIDTH_IN_MIN/MAX`,
 *  `OFFSET_IN_MIN/MAX`); a field left out of the patch is left as it was. */
export function placementPatch(p: Placement, patch: { widthIn?: number; xIn?: number | 'center'; yIn?: number | 'center' }): Placement {
  return {
    widthIn: patch.widthIn === undefined ? p.widthIn : clamp(patch.widthIn, WIDTH_IN_MIN, WIDTH_IN_MAX),
    xIn: patch.xIn === undefined ? p.xIn : patch.xIn === 'center' ? 'center' : clamp(patch.xIn, OFFSET_IN_MIN, OFFSET_IN_MAX),
    yIn: patch.yIn === undefined ? p.yIn : patch.yIn === 'center' ? 'center' : clamp(patch.yIn, OFFSET_IN_MIN, OFFSET_IN_MAX),
  };
}

/**
 * Does the print still fit inside its area? The negation of the engine's OWN clip test
 * (`printClipped`, the one `renderMockup` reports as `clipped`) — called rather than
 * restated, so the panel's amber line under Width and the footer's "Clipped" cannot
 * drift apart by a thousandth of an inch. It is here at all so the panel can warn
 * BEFORE the render lands, and can warn on a width the shop is still dragging.
 *
 * True with no art to measure: a document that has not been through the pipeline has no size to
 * place, and a warning about a print nobody can see yet is noise.
 */
export function printFits(area: PrintArea, placement: Placement, rotationDeg: number, art: { width: number; height: number } | undefined): boolean {
  if (!art || !(art.width > 0) || !(art.height > 0)) return true;
  return !printClipped(area, artRectIn(area, placement, art), rotationDeg);
}

/**
 * The placement section's size/resolution line, e.g. "10.0 × 12.5 in · 312
 * dpi on the photo". The art's height follows its own aspect ratio at the
 * placed width (`run.height / run.width`, the same relationship
 * `artRectIn` composites with); the dpi is the art's own resolution at that
 * placed size — `run.width` px spread over `placement.widthIn` in, the usual
 * print-dpi reading used everywhere else in the app (`printWidthFor`'s
 * inverse). `template` is accepted (not read) for parity with a future
 * clipped-aware line — today clipping is reported separately, in the status
 * line below.
 */
export function artSizeLine(run: RunResult, placement: Placement, template: MockupTemplate): string {
  void template;
  const widthIn = placement.widthIn;
  const heightIn = run.width > 0 ? widthIn * (run.height / run.width) : 0;
  const dpi = widthIn > 0 ? Math.round(run.width / widthIn) : 0;
  return `${widthIn.toFixed(1)} × ${heightIn.toFixed(1)} in · ${dpi} dpi on the photo`;
}

/**
 * Derives `job.templateId`/`job.garment` from the document's selected
 * colours (the first is the one on screen): snap the colour against the
 * CURRENT template's style, front view. Snapping to the current template
 * itself means "this is the blank's own colour" (`garment: null`, no
 * recolouring); snapping to a DIFFERENT blank of the same style switches the
 * job onto it (still `garment: null` — the photographed blank already is
 * that colour); no snap at all means an actual recolour, `garment` the raw
 * rgb — UNLESS the current template is `recolorable === false` (its mask
 * couldn't be told apart from the backdrop by colour, see `MockupTemplate`),
 * in which case recolouring it would just smear colour across the wrong
 * pixels, so the template's own colour is kept (`garment: null`) instead;
 * the picked colour still stays selected for other blanks of the style (a
 * colourway sheet render) to use. No colours selected is "own colour" on the
 * current template, same as a self-snap.
 *
 * The CURRENT template is tried on its own first, and that ordering is the
 * whole of the "duplicate loses to its built-in" fix: a style-wide snap
 * resolves ties to the first blank in registry order, and built-ins are merged
 * ahead of user templates — so a copy of a built-in (same photographed colour,
 * a different mask or print area) was swapped back for the original on the
 * first swatch click. Within the snap distance of the blank already chosen,
 * that blank is the answer; only a colour it does NOT wear may move the job
 * somewhere else. The same ordering keeps a template the user picked by hand
 * (`templateChange`) from bouncing straight back to a same-coloured sibling.
 */
export function garmentForColors(colors: string[], template: MockupTemplate, sources: TemplateSource[]): { templateId: string; garment: Rgb | null } {
  if (colors.length === 0) return { templateId: template.id, garment: null };
  const { rgb, snappedTo } = snapGarment(colors[0], candidatesFor(sources, template.style, 'front'));
  // The current template keeps the job when it wears the colour itself AND the
  // style-wide nearest blank is not a genuinely different colour: a user copy of a
  // built-in shares its exact garmentColor and must not lose to the original
  // (registry order), but a Navy blank 0.035 away from Black is a real neighbour
  // and the nearest one wins as it always did.
  const own = snapGarment(colors[0], [template]).snappedTo;
  if (own && (!snappedTo || toHex(snappedTo.garmentColor) === toHex(template.garmentColor))) return { templateId: template.id, garment: null };
  if (!snappedTo) return { templateId: template.id, garment: template.recolorable === false ? null : rgb };
  return { templateId: snappedTo.id, garment: null };
}

/**
 * The job and colour selection a template change produces. Picking a blank by
 * hand has to leave the two in step: `job.templateId`/`job.garment` are always
 * `garmentForColors`' answer for the colours in force, so the pressed swatch
 * still drives what is on screen (before this, a template change wrote
 * `templateId` alone and the preview could show one colour while another swatch
 * read as selected).
 *
 * A template of a DIFFERENT style clears the colours: the swatch row is rebuilt
 * from the new style's blanks, and a selection carried over from the old one
 * would sit under it unrenderable — every colour of it belongs to blanks this
 * style does not have.
 */
export function templateChange(
  job: MockupJob, colors: string[], next: MockupTemplate, current: MockupTemplate | undefined, sources: TemplateSource[],
): { job: MockupJob; colors: string[] } {
  const kept = current && current.style !== next.style ? [] : colors;
  const { templateId, garment } = garmentForColors(kept, next, sources);
  return { job: { ...job, templateId, garment }, colors: kept };
}

/** Labels for the two-button ink-look picker. */
/**
 * What the mockup panel's "Printed as" section calls each ink look — and what the
 * ticket prints (`lib/report-html.ts`).
 *
 * Deliberately NOT the top bar's words. The bar's "DTF" and "Screen print" choose
 * which TOOL the app is, and this chooses how the print is SIMULATED on a garment
 * in the mockup tool; two identical-looking pairs of buttons meaning different
 * things is the audit's #4, and the way out is to name each one for what it does.
 */
export const LOOK_LABELS: Record<InkLook, string> = { screen: 'Screen-print ink', dtf: 'DTF transfer' };


// ---- backdrop: what the panel's Backdrop section reads and writes -----------

/** The four states the Backdrop select offers. `'none'` is a job with no backdrop at all — the
 *  garment on the pixels it was photographed against, which is what every job starts as. */
export type BackdropKind = 'none' | 'color' | 'gradient' | 'scene';

/** The colour a fresh colour backdrop starts on: a light studio grey, near enough white to read as
 *  a sweep but far enough off it that the drop shadow is visible straight away. */
export const BACKDROP_DEFAULT_COLOR = '#f2f2f2';
/** A fresh gradient: white at the top falling to a mid grey, the shape of a lit studio wall. */
export const BACKDROP_DEFAULT_TOP = '#ffffff';
export const BACKDROP_DEFAULT_BOTTOM = '#c8c8c8';

/** Which of the select's four options a job is on. `null`/absent is `'none'` — see `sanitizeJob`,
 *  which makes those two one thing. */
export function backdropKind(b: Backdrop | null | undefined): BackdropKind {
  return b ? b.kind : 'none';
}

/**
 * One line naming the backdrop, for the inspector's row and the job ticket: `none`, `#3A6EA5`,
 * `gradient #FFFFFF → #C8C8C8`, or `scene Studio wall`.
 *
 * Hex UPPER-case, like every other hex the ticket prints (`sheetLabel`'s recolour caption), so a
 * ticket does not mix the two spellings of the same colour.
 *
 * A scene id `scenes` does not hold reads `scene (missing)` rather than the id: the pixels are not
 * there to render, the shop deleted them, and a UUID tells them nothing. (The store clears such a
 * backdrop the next time the job is written — `sanitizeJob` — so this is the window between a
 * delete and that write, plus any job carried in from elsewhere.)
 *
 * `options.applied: false` says the render is ignoring this backdrop (a blank whose mask does not
 * separate from what it was photographed against), and then the answer is `none` whatever the job
 * carries — a boolean rather than the template, so this stays a string function with no registry.
 */
export function describeBackdrop(
  b: Backdrop | null, scenes: ReadonlyArray<{ id: string; name: string }>, options?: { applied: boolean },
): string {
  // A backdrop the render IGNORES is not a backdrop this may name: the ticket is what the press
  // works from, and a row reading `#3A6EA5` beside a file showing the model's own studio wall
  // describes a garment nobody made. `applied: false` is the non-separable blank (see
  // `resolveBackdrop`, which returns null for it), where the job keeps its backdrop on purpose so
  // that going back to a flat-lay restores the swap.
  if (!b || options?.applied === false) return 'none';
  const hex = (rgb: Rgb) => toHex(rgb).toUpperCase();
  if (b.kind === 'color') return hex(b.rgb);
  if (b.kind === 'gradient') return `gradient ${hex(b.top)} → ${hex(b.bottom)}`;
  const scene = scenes.find((s) => s.id === b.sceneId);
  return scene ? `scene ${scene.name}` : 'scene (missing)';
}

/**
 * The backdrop a change of KIND produces, on defaults the shop can then adjust — what the Backdrop
 * select writes.
 *
 * The kind already in force returns the very same object (not an equal one): the panel writes this
 * straight into the job, and a rebuilt-but-identical backdrop would cost a render for no change
 * (`mockupChangeKind` compares field by field, but the guard costs nothing here).
 *
 * `'scene'` with no scenes imported is `null`, not a backdrop naming pixels that do not exist — the
 * panel's Scene row is what offers the import, and the first one added is what turns it on.
 */
export function withBackdropKind(current: Backdrop | null, kind: BackdropKind, scenes: ReadonlyArray<{ id: string }>): Backdrop | null {
  if (kind === backdropKind(current)) return current;
  switch (kind) {
    case 'color': return { kind: 'color', rgb: parseColor(BACKDROP_DEFAULT_COLOR) };
    case 'gradient': return { kind: 'gradient', top: parseColor(BACKDROP_DEFAULT_TOP), bottom: parseColor(BACKDROP_DEFAULT_BOTTOM) };
    case 'scene': return scenes.length ? { kind: 'scene', sceneId: scenes[0].id } : null;
    case 'none': return null;
    default: {
      // Exhaustive: a kind added to `BackdropKind` without a default here is a compile error rather
      // than a silent "no backdrop".
      const never: never = kind;
      return never;
    }
  }
}

/**
 * The kind the Backdrop select SHOWS. The job's own, except while a pending choice is being held
 * over a job that carries no backdrop at all — which is the one case the two can differ, see
 * `nextPendingKind`. Once the job has a kind, that kind is the truth and any pending one is spent.
 */
export function shownBackdropKind(jobKind: BackdropKind, pendingKind: BackdropKind | null): BackdropKind {
  return jobKind === 'none' && pendingKind ? pendingKind : jobKind;
}

/**
 * The pending kind to hold after the shop picks `next` in the Backdrop select.
 *
 * `'scene'` with nothing imported yet is the one choice that writes NO backdrop (there is no scene
 * to name, so `withBackdropKind` is `null`), so it has to be remembered: without this the select
 * snapped straight back to None — from a colour or gradient job as well as from none — and the
 * "Add scene…" button behind the Scene row, the only route to a first backdrop photo, could not be
 * reached at all.
 *
 * Every other choice writes a backdrop or clears one, so nothing is held and the job's own kind is
 * what the select reads back.
 */
export function nextPendingKind(next: BackdropKind, scenes: ReadonlyArray<{ id: string }>): BackdropKind | null {
  return next === 'scene' && scenes.length === 0 ? 'scene' : null;
}

/** Said in the panel when the blank's mask cannot be told apart from what it was photographed
 *  against: there is then no trustworthy edge to put a new backdrop up to, and the worker ignores
 *  the job's backdrop for exactly the same reason (`resolveBackdrop`). Named for the BACKDROP —
 *  `lib/template-editor.ts` has a `NOT_SEPARABLE_HINT` of its own, about recolouring. */
export const BACKDROP_NOT_SEPARABLE_HINT = 'Backdrop swap needs a separable mask';


// ---- more prints: one extra print per non-primary area ---------------------

/** The print this job puts in `areaId`, or undefined for an area it leaves bare. The PRIMARY
 *  area never has one — that print is `job.placement`, which is why the panel's "More prints"
 *  section starts at the second area. */
export function extraPrintFor(job: MockupJob, areaId: string): MockupPrint | undefined {
  return (job.extraPrints ?? []).find((p) => p.areaId === areaId);
}

/** What one row of the "More prints" section can change about a print: any of its fields, and the
 *  placement field by field (a width slider must not have to restate the offsets). */
export interface ExtraPrintPatch {
  docId?: string | null;
  placement?: Partial<Placement>;
  rotationDeg?: number;
  enabled?: boolean;
}

/**
 * The size and position a print is first given in an area: a little over half the area wide and
 * centred across it — visibly INSIDE the area (so the first render shows it where the shop put it
 * rather than clipped at an edge), and numeric rather than `'center'` so the row's X field has
 * something to show and to nudge.
 *
 * With the art's pixel size in hand the width is ALSO capped so the art's own height fits (nine
 * tenths of the area's), and the print is centred down the area rather than guessed a fifth of the
 * way in — a tall design in a square sleeve would otherwise be born clipped at the bottom. Without
 * it (no run yet, or a caller with no document to measure) the width is the plain fraction and the
 * fifth-down guess stands; the shop's next nudge of the Y slider settles it either way.
 */
export function defaultExtraPlacement(area: PrintArea, art?: { width: number; height: number }): Placement {
  const round = (v: number) => Math.round(v * 10) / 10;
  const fits = art && art.width > 0 && art.height > 0 ? (area.heightIn * 0.9 * art.width) / art.height : Infinity;
  const widthIn = Math.max(WIDTH_IN_MIN, round(Math.min(area.widthIn * 0.6, fits)));
  const heightIn = art && art.width > 0 ? (widthIn * art.height) / art.width : 0;
  return {
    widthIn,
    xIn: round((area.widthIn - widthIn) / 2),
    yIn: art ? round(Math.max(0, (area.heightIn - heightIn) / 2)) : round(area.heightIn * 0.2),
  };
}

/**
 * The job with `areaId`'s extra print added, patched or (for a null patch) removed — the single
 * write path behind every control in the "More prints" section.
 *
 * A patch for an area with no print yet CREATES one, on `defaultExtraPlacement`, enabled, so
 * choosing a design in the row's select is all it takes to get a print.
 *
 * The template, when the caller has one, is checked against the area being WRITTEN and nothing
 * else: a row may only be created in an area this blank actually has, and never in the PRIMARY one
 * (which is `job.placement`, not an extra print at all). It is deliberately NOT used to filter the
 * list, because a job moved onto a blank with no sleeve KEEPS its sleeve print — the render
 * reports the area as skipped and the ticket says so — and editing the pocket must not throw the
 * sleeve away on the way past.
 *
 * The result still goes through `sanitizeJob` (without the template, exactly as the store's own
 * setter does) so the placement, the rotation and the one-print-per-area rule hold.
 */
export function setExtraPrint(job: MockupJob, areaId: string, patch: ExtraPrintPatch | null, template?: MockupTemplate): MockupJob {
  const prints = job.extraPrints ?? [];
  const areas = template ? areasOf(template) : undefined;
  // `slice(1)`: the primary area is the job's own print, so it is not one this can write to.
  const offered = areas ? areas.slice(1).find((a) => a.id === areaId) : undefined;
  // An area this blank does not offer is not something the panel can have a row for, so there is
  // nothing to write — and, in particular, nothing to DELETE: the rest of the job stands.
  if (areas && !offered) return sanitizeJob(job);
  if (!patch) return sanitizeJob({ ...job, extraPrints: prints.filter((p) => p.areaId !== areaId) });
  const current = prints.find((p) => p.areaId === areaId);
  const base: MockupPrint = current ?? {
    areaId,
    docId: null,
    placement: offered ? defaultExtraPlacement(offered) : { widthIn: WIDTH_IN_MIN, xIn: 0, yIn: 0 },
    rotationDeg: 0,
    enabled: true,
  };
  const next: MockupPrint = {
    ...base,
    docId: patch.docId === undefined ? base.docId : patch.docId,
    placement: { ...base.placement, ...patch.placement },
    rotationDeg: patch.rotationDeg ?? base.rotationDeg,
    enabled: patch.enabled ?? base.enabled,
  };
  const extraPrints = current ? prints.map((p) => (p.areaId === areaId ? next : p)) : [...prints, next];
  return sanitizeJob({ ...job, extraPrints });
}

/**
 * What one area's "Design for …" select offers: nothing, this document's own art, or another open
 * document's. The same rule as the back-design select — a document has to have been through the
 * pipeline (`run`) to have art to composite — and the same "None" first entry, since removing a
 * print is what the select is also for.
 *
 * `'self'` rather than the document's own id: the stored `docId` for "this file" is `null`, and
 * the two are the same print to the compositor (`extraArtsFor`), so the panel must not write an id
 * that would go stale the moment the document is closed and reopened.
 */
export function extraDesignOptions(docs: DocEntry[], selfId: string): Array<{ value: string; label: string }> {
  return [
    { value: '', label: 'None' },
    { value: 'self', label: 'This file' },
    ...docs.filter((d) => d.id !== selfId && d.status === 'ready' && d.run).map((d) => ({ value: d.id, label: d.name })),
  ];
}

/** One print of a job, resolved against the blank it is rendered on. */
export interface JobPrint {
  areaId: string;
  /** What the area is called, for a control's label and an inspector row. */
  name: string;
  area: PrintArea;
  /** `null` = this document's own art, exactly as `MockupPrint.docId` means it. */
  docId: string | null;
  placement: Placement;
  rotationDeg: number;
  enabled: boolean;
  /** The job's own print — `job.placement`, in the blank's first area. */
  primary: boolean;
}

/**
 * Every print this job draws, the primary one first and each extra in the order it composites —
 * what the inspector lists a row per, and what the viewer outlines.
 *
 * An extra naming an area the blank does not have is LEFT OUT, because the compositor leaves it
 * out too (it comes back in the render's `skipped`, which the inspector reports separately): a row
 * for it would describe a print nobody can see.
 */
export function jobPrints(job: MockupJob, template: MockupTemplate): JobPrint[] {
  const areas = areasOf(template);
  const nameOf = (a: PrintArea) => a.name ?? (a.id ? titleCaseId(a.id) : '');
  const primary = areas[0];
  const out: JobPrint[] = [{
    areaId: primary.id ?? '',
    name: nameOf(primary),
    area: primary,
    docId: null,
    placement: job.placement,
    rotationDeg: job.rotationDeg ?? 0,
    enabled: true,
    primary: true,
  }];
  for (const p of job.extraPrints ?? []) {
    const area = areas.find((a, i) => i > 0 && a.id === p.areaId);
    if (!area) continue;
    out.push({ areaId: p.areaId, name: nameOf(area), area, docId: p.docId, placement: p.placement, rotationDeg: p.rotationDeg, enabled: p.enabled, primary: false });
  }
  return out;
}

/**
 * One print's line in the inspector: `10.0 × 12.5 in · 102 dpi`, plus `· rotated 15°` when it is
 * turned. The height follows the art's own aspect ratio at the placed width and the dpi is its
 * resolution there — the same two readings `artSizeLine` takes, without the "on the photo" tail,
 * because these rows are already headed by the area they describe.
 *
 * `undefined` art is a print whose document is closed or has not been run: it says so rather than
 * dividing by zero, and the render reports the same print as skipped.
 */
export function printSizeLine(placement: Placement, rotationDeg: number, art: { width: number; height: number } | undefined): string {
  if (!art || !(art.width > 0)) return 'no design';
  const widthIn = placement.widthIn;
  const heightIn = widthIn * (art.height / art.width);
  const dpi = widthIn > 0 ? Math.round(art.width / widthIn) : 0;
  const turn = rotationDeg ? ` · rotated ${rotationDeg}°` : '';
  return `${widthIn.toFixed(1)} × ${heightIn.toFixed(1)} in · ${dpi} dpi${turn}`;
}

/** Why a render left a print out, in the two words the shop has to act on: the design is not
 *  there to print (closed, or never run), or this blank simply has no such area — which is a
 *  print RETAINED from another blank, and the fix is to remove it or go back. */
export type SkipReason = 'no design' | 'no such area on this blank';

/** One area of `DocMockup.skipped`, named and explained. */
export interface SkippedPrint { areaId: string; name: string; reason: SkipReason }

/**
 * The render's `skipped` area ids as the inspector and the job ticket say them: what the area is
 * called, and WHICH of the compositor's two reasons applies — decided here, against the blank on
 * screen, rather than carried through the render (the compositor reports one flat list, and both
 * causes are "this print was not made" to it).
 *
 * `areaById` is the whole test: an area this blank HAS that still came back skipped had no art to
 * print, and one it does not have was never printable here at all. Without a template (nothing to
 * check against) every skip reads as the missing design it usually is.
 */
export function skippedPrints(skipped: ReadonlyArray<string>, template: MockupTemplate | null | undefined): SkippedPrint[] {
  return skipped.map((areaId) => {
    const area = template ? areaById(template, areaId) : undefined;
    return {
      areaId,
      name: area?.name ?? (areaId ? titleCaseId(areaId) : ''),
      reason: template && !area ? 'no such area on this blank' : 'no design',
    };
  });
}

/**
 * The job's prints for areas THIS blank does not have: kept on purpose (a job moved to a blank
 * with no sleeve keeps its sleeve print, per §14.1's closed-doc policy) but invisible to the
 * "More prints" rows, which only offer the areas the blank HAS. The panel lists these separately
 * so a print retained from another blank can be seen and removed here rather than only by going
 * back to the blank it came from.
 *
 * Named from the id alone (`titleCaseId`): the area is not on this template, so there is no
 * authored name to read. Empty without a template — nothing to compare against, and the panel has
 * nothing to render rows beside either.
 */
export function retainedPrints(job: MockupJob, template: MockupTemplate | null | undefined): Array<{ areaId: string; name: string; print: MockupPrint }> {
  if (!template) return [];
  return (job.extraPrints ?? [])
    .filter((p) => !areaById(template, p.areaId))
    .map((p) => ({ areaId: p.areaId, name: p.areaId ? titleCaseId(p.areaId) : '', print: p }));
}

/** The job without the print in `areaId` — the Remove button behind a retained print's row.
 *  Written straight rather than through `setExtraPrint`, which (by design) refuses to touch an
 *  area the blank does not offer and so cannot delete one either. The store's own `setMockupJob`
 *  sanitises what this returns, exactly as it does every other panel write. */
export function withoutPrint(job: MockupJob, areaId: string): MockupJob {
  return { ...job, extraPrints: (job.extraPrints ?? []).filter((p) => p.areaId !== areaId) };
}
